/* assets/soho-video-config.js  —  SOHO Bohemian
 *
 * Lets the dashboard drive the two video surfaces without a theme edit.
 *
 * ─────────────────────────────── THE CONTRACT ───────────────────────────────
 * The theme is the FLOOR. Every card on the page was already rendered by
 * Liquid from the section's own blocks, and it is already correct. This file
 * fetches `/api/public/video-config` from the dashboard and, only if that call
 * succeeds and returns a real config, OVERLAYS it.
 *
 * Any failure whatsoever — no network, CORS, a timeout, malformed JSON, a
 * document nobody ever wrote, an exception halfway through the swap — leaves
 * the page exactly as Liquid rendered it. There is no error state on the
 * storefront, because there is no state to be in: the theme's own markup is
 * always a complete, working answer.
 *
 * The swap is also REVERSIBLE. Before touching a track or a bubble list the
 * old innerHTML is kept, and any exception during the rebuild puts it back.
 * A half-built row never reaches a shopper.
 *
 * ────────────────────── HOW THE SHOPPING GETS IN (29/08) ────────────────────
 * This file builds no shop bar, no strip, no card and no modal. For every item
 * the dashboard sends it writes ONE `<script type="application/json"
 * data-soho-shop>` inside the card — the payload of §1 of
 * docs/CONTRATO-SHOP-VIDEO.md, byte for byte the same tag the Liquid writes for
 * a theme block — and stops there. `window.SohoShopVideo` reads that tag and
 * owns every pixel of the strip and of the modal.
 *
 * That replaces a structural defect. The old file CLONED the shop bar Liquid
 * had rendered, which meant two things nobody wanted: a store whose theme
 * blocks had no product had nothing to clone (`buyProto === null`), so NO
 * dashboard story could sell anything at all; and no dashboard item could ever
 * sell a product the theme had not already rendered — which is exactly the
 * multi-product case Dylan asked for ("the girls have an outfit full of many
 * items"). There is one path now, not two: Liquid and dashboard both speak the
 * payload, and the modal is the only thing that turns it into pixels.
 *
 * Two consequences worth knowing before editing this file:
 *   • a card cloned from a theme block carries THAT block's payload script, so
 *     every rebuilt card has its own written (or removed) before it is
 *     inserted. A stale payload would sell the wrong product;
 *   • a dashboard product needs a bag to be clicked. `soho-video-reels.js`
 *     delegates on `[data-soho-reel-bag]`, so when the theme rendered no bag
 *     anywhere one is built here — see buildBag().
 *
 * ──────────────────────────────── SCOPE ─────────────────────────────────────
 *   • the reel row / wall is `#SohoReels--<id>` and `.soho-reel*`;
 *   • the story dock is `<soho-story-dock>` and `.soho-dock*`.
 * Nothing outside those roots is read or written. In particular there is no
 * `document.querySelectorAll('video')` here — the nine live set pages and the
 * home hero own videos this file must never see.
 *
 * ────────────────────────── NOT IN THE THEME EDITOR ─────────────────────────
 * In design mode this file does nothing. Two editors fighting over one preview
 * is how a merchant concludes the theme editor is broken: inside the editor the
 * blocks he is dragging are the truth, and on the live store the dashboard is.
 *
 * ─────────────────────────────── ESCAPE HATCH ───────────────────────────────
 *   ?soho_video_config=off   — ignore the dashboard for this pageview.
 *   ?soho_video_debug=1      — narrate every decision to the console.
 */
(function () {
  'use strict';

  /* Both sections load this file, so on a page with the row AND the dock it is
     in the DOM twice — and two identical <script src> tags both execute. One
     pass handles every surface on the page, so the second copy stands down. */
  if (window.__sohoVideoConfigBooted) return;
  window.__sohoVideoConfigBooted = true;

  if (!window.fetch || !window.Promise || !document.querySelector) return;
  if (window.Shopify && window.Shopify.designMode) return;

  var SCRIPT = document.currentScript;

  var ENDPOINT =
    window.SOHO_VIDEO_CONFIG_ENDPOINT ||
    (SCRIPT && SCRIPT.getAttribute('data-endpoint')) ||
    'https://soho-dashboard--soho-dashboard-a11f0.us-central1.hosted.app/api/public/video-config';

  /* Passed by the section so this file never has to guess an asset URL. Only
     needed in one case: the theme rendered stills only, so the section did not
     load the reel player, and the dashboard has now supplied videos. */
  var REELS_JS = (SCRIPT && SCRIPT.getAttribute('data-reels-js')) || '';

  /* The same trapdoor, one surface further along. Contract §8 has both sections
     load the modal unconditionally, in which case this attribute is never used;
     it exists because the reel player was gated on `video_count > 0` for months
     and the identical mistake here — gating the modal on "some block has a
     product" — would silently make every dashboard-supplied product unsellable.
     Absent attribute, absent behaviour: nothing is guessed. */
  var SHOP_JS = (SCRIPT && SCRIPT.getAttribute('data-shop-js')) || '';

  var TIMEOUT_MS = 2500;
  var CACHE_MS = 60000;

  var CROP = { top: '50% 12%', center: '50% 50%', bottom: '50% 88%' };
  var DEFAULT_CROP = CROP.top;

  var QUERY = window.location.search || '';
  var DEBUG = /[?&]soho_video_debug=1/.test(QUERY);
  if (/[?&]soho_video_config=off/.test(QUERY)) {
    if (DEBUG) console.log('[soho-video-config] disabled by query string');
    return;
  }

  function log() {
    if (!DEBUG) return;
    try {
      console.log.apply(console, ['[soho-video-config]'].concat([].slice.call(arguments)));
    } catch (e) {
      /* console is not a guarantee */
    }
  }

  /* ------------------------------------------------------------- helpers -- */

  function els(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  /** Editor bookkeeping must not be cloned: a duplicated block id makes the
      theme editor's block picker point at cards that are not blocks. */
  function stripEditorAttrs(node) {
    var all = [node].concat(els(node, '*'));
    for (var i = 0; i < all.length; i++) {
      var attrs = all[i].attributes;
      for (var j = attrs.length - 1; j >= 0; j--) {
        var name = attrs[j].name;
        if (name.indexOf('data-shopify') === 0 || name === 'id') all[i].removeAttribute(name);
      }
    }
  }

  function setText(node, value) {
    if (!node || !value) return;
    node.textContent = value;
  }

  function drop(node) {
    if (node && node.parentNode) node.parentNode.removeChild(node);
  }

  /** Custom elements read their children once, in connectedCallback. Taking the
      element out and putting it straight back is the documented way to make
      that run again over markup we just replaced. */
  function reattach(el) {
    var parent = el.parentNode;
    if (!parent) return;
    var next = el.nextSibling;
    parent.removeChild(el);
    parent.insertBefore(el, next);
  }

  function loadOnce(src) {
    if (!src) return;
    if (document.querySelector('script[data-soho-lazy="' + src + '"]')) return;
    var s = document.createElement('script');
    s.src = src;
    s.defer = true;
    s.setAttribute('data-soho-lazy', src);
    document.head.appendChild(s);
  }

  /* ------------------------------------------------- the shoppable payload -- */

  /*
   * Everything between here and `writeShopPayload` turns the PUBLIC config
   * (contract §6: products[] with handle/title/url/imageUrl/priceCents/
   * available, plus collection) into the SHORT card payload (§1: h/t/u/i/pr/
   * cp/a). Two rules from the contract are enforced on this side as well as on
   * the server's, deliberately and not by accident:
   *
   *   §1 rule 2 — every handle is re-validated here and every URL is DERIVED
   *   from the handle. `/api/public/video-config` is unauthenticated; "the
   *   dashboard would never write that" is hope, not a guarantee, and this is
   *   the last checkpoint before a string becomes an `href` on the live store.
   *
   *   §1 rule 3 — twelve products per video, the same ceiling the Liquid
   *   schema, the editor, the reader and the Firestore rule all impose.
   */
  /* The `_` is legal in a Shopify handle. Leaving it out of this class first
     shipped as a silent drop of `untitled-apr29_14-55` — "The Ela Top", $139,
     1 of the store's 81 products. What the class bars is `/`, `.`, `:` and
     `%`; `_` is none of them. */
  var HANDLE_RE = /^[a-z0-9][a-z0-9_-]{0,254}$/;
  var MAX_SHOP_PRODUCTS = 12;

  /* A product URL, ours or somebody's, reduced back to the handle it contains.
     `/collections/x/products/y` is a real Shopify PDP path, so the handle is
     matched wherever `products/` appears rather than only at the start. */
  var PDP_RE = /(?:^|\/)products\/([a-z0-9][a-z0-9_-]{0,254})(?:[/?#]|$)/;

  /* NOTE the absence of `price` in P_PRICE, and it is not an oversight: the
     legacy `price` field of the config is a FORMATTED STRING ("$58.00"), and
     reading it as a number of cents would price a $58 bikini at 58 cents. §1
     rule 1: cents are integers, and a formatted string is never one. */
  var P_HANDLE = ['handle', 'productHandle', 'product_handle', 'h'];
  var P_TITLE = ['title', 'productTitle', 'product_title', 'name', 't'];
  var P_IMAGE = ['imageUrl', 'image', 'productImage', 'product_image', 'i'];
  var P_PRICE = ['priceCents', 'price_cents', 'pr'];
  var P_COMPARE = ['compareAtCents', 'compare_at_cents', 'compareAtPriceCents', 'cp'];

  var C_HANDLE = ['handle', 'h'];
  var C_HANDLE_FLAT = ['collection_handle', 'collectionHandle'];
  var C_TITLE = ['title', 'name', 't', 'collection_title', 'collectionTitle'];
  var C_COUNT = ['count', 'productsCount', 'products_count', 'n'];

  function str(value, max) {
    return typeof value === 'string' ? value.trim().slice(0, max) : '';
  }

  /** First non-empty spelling wins — the same forgiveness the server reader
      practises, for the same reason: a field merely spelled differently must
      cost one field, never the whole strip. */
  function pick(bag, keys, max) {
    for (var i = 0; i < keys.length; i++) {
      var value = str(bag[keys[i]], max);
      if (value) return value;
    }
    return '';
  }

  function handleOf(bag, keys) {
    var raw = pick(bag, keys, 255).toLowerCase();
    return HANDLE_RE.test(raw) ? raw : '';
  }

  /**
   * A URL this file is willing to put in an `href`.
   * `javascript:` and `data:` are the whole reason it exists; `//host/x` is
   * refused because a protocol-relative URL is an absolute one wearing a
   * costume, and `/\evil.com` is the same costume in a different fabric (the
   * browser normalises the backslash and leaves the shop).
   */
  function safeHref(value) {
    var raw = str(value, 2048);
    if (!raw) return '';
    if (raw.indexOf('//') === 0) return '';
    if (raw.indexOf('/\\') === 0) return '';
    if (raw.charAt(0) === '/') return raw;
    try {
      var u = new URL(raw);
      if (u.protocol === 'https:' || u.protocol === 'http:') return u.href;
    } catch (e) {
      /* not a URL */
    }
    return '';
  }

  /** Same list plus protocol-relative, which §1 allows for `i` and which the
      Shopify CDN still emits in older stored records. `src` is not `href`: it
      fetches a picture, it does not navigate. */
  function mediaSrc(value) {
    var raw = str(value, 2048);
    if (!raw) return '';
    if (raw.indexOf('//') === 0 && raw.indexOf('/\\') !== 0) return raw;
    return safeHref(raw);
  }

  /** The handle inside a product URL, but only when the URL is OURS. A handle
      lifted off another host would build a link to a product this store does
      not have. */
  function pdpHandle(value) {
    var raw = str(value, 2048).toLowerCase();
    if (!raw) return '';
    var path = raw;
    if (raw.charAt(0) !== '/') {
      try {
        var u = new URL(raw, window.location.href);
        if (u.hostname !== window.location.hostname) return '';
        path = u.pathname;
      } catch (e) {
        return '';
      }
    } else if (raw.indexOf('/\\') === 0) {
      return '';
    }
    var match = PDP_RE.exec(path);
    return match ? match[1] : '';
  }

  /** Cents, as an integer, or null. A numeric string is accepted because JSON
      written by hand is where the round-trip through a form input shows up. */
  function centsOf(bag, keys) {
    for (var i = 0; i < keys.length; i++) {
      var value = bag[keys[i]];
      if (typeof value === 'number' && isFinite(value) && value >= 0 && value < 1e9) {
        return Math.round(value);
      }
      if (typeof value === 'string' && /^[0-9]{1,9}$/.test(value.trim())) {
        return parseInt(value.trim(), 10);
      }
    }
    return null;
  }

  function countOf(bag, keys) {
    for (var i = 0; i < keys.length; i++) {
      var value = bag[keys[i]];
      if (typeof value === 'number' && isFinite(value) && value >= 0 && value < 1e6) {
        return Math.round(value);
      }
    }
    return null;
  }

  /** A last-resort title. Shopify derives handles FROM titles, so undoing the
      dashes is a reading of the merchant's own words rather than an invention —
      and the modal overwrites it with the real one the moment it hydrates the
      card from `/products/{h}.js` (§5). Better than a nameless card. */
  function titleFromHandle(handle) {
    return handle.replace(/-+/g, ' ').replace(/\s+/g, ' ').trim();
  }

  function isBag(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  /** One entry of `products[]` (§6) as one item of `p[]` (§1), or null. */
  function shopProduct(entry) {
    if (!isBag(entry)) return null;
    var handle = handleOf(entry, P_HANDLE);
    if (!handle) return null;

    var out = {
      h: handle,
      t: pick(entry, P_TITLE, 160) || titleFromHandle(handle),
      /* §1 rule 2: derived, never copied out of a free text field. */
      u: '/products/' + handle
    };

    var image = mediaSrc(pick(entry, P_IMAGE, 2048));
    if (image) out.i = image;

    var price = centsOf(entry, P_PRICE);
    if (price !== null) out.pr = price;

    /* A compare-at that is not ABOVE the price is not a discount; struck
       through next to an equal or higher price it reads as a mistake. */
    var compare = centsOf(entry, P_COMPARE);
    if (compare !== null && (price === null || compare > price)) out.cp = compare;

    /* §1: `a` defaults to true, so only the explicit false is worth sending. */
    if (entry.available === false || entry.a === false) out.a = false;

    return out;
  }

  /** The collection card (§1 `c`), from the nested object the route serves or
      from the flat `collection_handle` the stored document uses. */
  function shopCollection(item) {
    var nested = isBag(item.collection) ? item.collection : null;
    var bag = nested;
    var handle = nested ? handleOf(nested, C_HANDLE) : '';
    if (!handle) {
      handle = handleOf(item, C_HANDLE_FLAT);
      bag = item;
    }
    if (!handle) return null;

    var out = {
      h: handle,
      t: pick(bag, C_TITLE, 160) || titleFromHandle(handle),
      u: '/collections/' + handle
    };
    var count = countOf(bag, C_COUNT);
    if (count !== null) out.n = count;
    return out;
  }

  /**
   * The one product a pre-multi-product config can express: the four flat
   * legacy fields. It matters during the deploy window, when the theme is new
   * and the endpoint is not (or the other way round) — and it is the reason a
   * store mid-rollout sells one product instead of none.
   */
  function legacyProduct(item) {
    var handle = pdpHandle(item.productUrl);
    if (!handle) return null;

    var out = {
      h: handle,
      t: str(item.productTitle, 160) || str(item.caption, 160) || str(item.label, 160) || titleFromHandle(handle),
      u: '/products/' + handle
    };
    var image = mediaSrc(item.productImageUrl);
    if (image) out.i = image;
    return out;
  }

  /**
   * The whole §1 payload for one config item, or null when there is nothing to
   * sell. Null is a legitimate, common answer: §1 rule 4 says a card with no
   * products and no collection gets no modal and keeps its plain `<a href>`.
   */
  function shopPayloadFor(item) {
    var products = [];
    var seen = {};
    var raw = Array.isArray(item.products) ? item.products : null;

    if (raw) {
      for (var i = 0; i < raw.length; i++) {
        if (products.length >= MAX_SHOP_PRODUCTS) break;
        var product = shopProduct(raw[i]);
        /* '#' prefixes the key so a handle can never collide with something on
           Object.prototype. Handles have no underscores, so `__proto__` cannot
           arrive — this costs one character and removes the argument. */
        if (!product || seen['#' + product.h]) continue;
        seen['#' + product.h] = true;
        products.push(product);
      }
    }

    if (!products.length) {
      var legacy = legacyProduct(item);
      if (legacy) products.push(legacy);
    }

    var collection = shopCollection(item);
    if (!products.length && !collection) return null;

    var payload = {
      t:
        str(item.caption, 160) ||
        str(item.label, 160) ||
        (products.length ? products[0].t : collection.t),
      p: products
    };
    /* §1: `c` is "object OR absent". An explicit `undefined` would survive into
       some readers as a present-but-empty key. */
    if (collection) payload.c = collection;
    return payload;
  }

  /** How many things this card can sell — products plus the collection card.
      The same arithmetic as `videoShopTargetCount()` in the dashboard. */
  function shopCount(payload) {
    if (!payload) return 0;
    return (payload.p ? payload.p.length : 0) + (payload.c ? 1 : 0);
  }

  /** Where the bag points when the modal is not there to intercept the click:
      the first product, or the collection when the video sells only that. */
  function primaryHref(payload) {
    if (!payload) return '';
    if (payload.p && payload.p.length) return payload.p[0].u;
    if (payload.c) return payload.c.u;
    return '';
  }

  function firstCents(payload) {
    if (payload && payload.p && payload.p.length && typeof payload.p[0].pr === 'number') {
      return payload.p[0].pr;
    }
    return null;
  }

  /**
   * Writes the card's payload, and — just as important — removes the one it
   * inherited. Every card here is a CLONE of a card Liquid rendered, so it
   * arrives carrying that block's `<script data-soho-shop>`; left in place it
   * would sell the donor's product from under a different video.
   * Returns how many things the card can now sell.
   */
  function writeShopPayload(card, payload) {
    var stale = els(card, 'script[data-soho-shop]');
    for (var i = 0; i < stale.length; i++) drop(stale[i]);
    if (!payload) return 0;

    var node = document.createElement('script');
    node.type = 'application/json';
    node.setAttribute('data-soho-shop', '');
    /* The JSON is inserted as a text node, so no parser ever sees markup here.
       Escaping `<` anyway costs nothing and keeps the card correct if it is
       ever round-tripped through innerHTML — which this very file does when it
       snapshots and restores a track. */
    node.textContent = JSON.stringify(payload).replace(/</g, '\\u003c');
    card.appendChild(node);
    return shopCount(payload);
  }

  /**
   * Money the way the theme prints it, or ''. Never a string invented here: a
   * hard-coded "$" in a store that also sells in CAD is a wrong price, and a
   * wrong price is worse than no price. `formatMoney` is measured to exist in
   * Broadcast 7.1.1 (contract §3); the guard is for the minute it does not.
   * `money_format` may legally contain markup (`<span class=money>`), and this
   * lands in textContent, so tags are stripped rather than printed.
   */
  function money(cents) {
    if (typeof cents !== 'number' || !isFinite(cents)) return '';
    try {
      var theme = window.theme;
      if (!theme || typeof theme.formatMoney !== 'function') return '';
      var out = theme.formatMoney(cents, theme.moneyFormat);
      if (typeof out !== 'string') return '';
      return out.replace(/<[^>]*>/g, '').trim();
    } catch (e) {
      /* the theme's helper is a courtesy, not a dependency */
      return '';
    }
  }

  /* ------------------------------------------------------------- the row -- */

  /**
   * Last-resort bag glyph. The theme's own `icon-bag` snippet is preferred and
   * cloned whenever ANY card in the section has a bag; this string runs only
   * when the merchant's theme blocks have no product at all, i.e. exactly the
   * case the old clone-the-bar design could not sell in. The section's scoped
   * rule `.soho-reel__bag .icon path { stroke: currentColor; fill: none }`
   * paints it, so the outline follows the disc like the snippet's does.
   */
  var BAG_SVG =
    '<svg class="icon icon-bag" viewBox="0 0 24 24" role="presentation" focusable="false" aria-hidden="true">' +
    '<path d="M5.7 8.3h12.6l-1.1 11.9a1.7 1.7 0 0 1-1.7 1.5H8.5a1.7 1.7 0 0 1-1.7-1.5L5.7 8.3Z" stroke-width="1.4" stroke-linejoin="round"/>' +
    '<path d="M9.3 9.5V6.7a2.7 2.7 0 0 1 5.4 0v2.8" stroke-width="1.4" stroke-linecap="round"/>' +
    '</svg>';

  /**
   * A bag anchor for a card whose donor had none. It is a 44x44 target drawn
   * over a 32px disc — the section's own CSS does that, because the classes are
   * the section's. Nothing here positions anything; if the section's stylesheet
   * is missing the anchor is still a link to the PDP, which is the floor.
   */
  function buildBag(bagProto) {
    var bag;
    if (bagProto) {
      bag = bagProto.cloneNode(true);
      stripEditorAttrs(bag);
    } else {
      bag = document.createElement('a');
      bag.className = 'soho-reel__bag';
      bag.setAttribute('data-soho-reel-bag', '');
      var disc = document.createElement('span');
      disc.className = 'soho-reel__bag__disc';
      /* A constant literal with nothing interpolated into it. */
      disc.innerHTML = BAG_SVG;
      bag.appendChild(disc);
    }
    bag.removeAttribute('data-soho-shop-count');
    return bag;
  }

  /** The handle a theme-rendered card is already selling, or ''. Its own
      payload is the truth; the bag's href is the fallback for a theme that has
      not been redeployed with §1 in it yet. */
  function cardHandle(card) {
    var script = card.querySelector('script[data-soho-shop]');
    if (script) {
      try {
        var data = JSON.parse(script.textContent || 'null');
        if (data && Array.isArray(data.p) && data.p.length) {
          var handle = str(data.p[0].h, 255).toLowerCase();
          if (HANDLE_RE.test(handle)) return handle;
        }
      } catch (e) {
        /* a payload we cannot read is a payload we cannot match on */
      }
    }
    var bag = card.querySelector('[data-soho-reel-bag]') || card.querySelector('.soho-reel__bag');
    return bag ? pdpHandle(bag.getAttribute('href')) : '';
  }

  /** What the bag says out loud. One product keeps the old sentence; several
      say how many, because "Shop this" under a bag that opens a strip of five
      is a promise the screen reader cannot see broken. */
  function bagLabel(payload, item) {
    if (payload) {
      var count = shopCount(payload);
      if (count > 1) return 'Shop ' + count + ' items in this video' + (payload.t ? ' — ' + payload.t : '');
      if (payload.p.length) return 'Shop ' + payload.p[0].t;
      return 'Shop the ' + payload.c.t + ' collection';
    }
    return 'Shop this' + (item.caption ? ' — ' + item.caption : '');
  }

  /**
   * One reel card, built by CLONING a card the theme already rendered rather
   * than by writing markup here. The clone keeps every class, wrapper and
   * modifier the section decided on — overlay captions, the aspect ratio, the
   * colour surface — so the dashboard changes WHAT is in the row and never how
   * the row looks. It also means this file cannot drift from the Liquid: if a
   * card gains a part tomorrow, the clone gains it too.
   *
   * The donor is chosen, not fixed. When the theme already rendered a card for
   * this item's first product, THAT card is cloned, and its live price snippet
   * (compare-at, price_varies, sold out — none of which a dashboard field can
   * express) is this product's real money and is kept. Otherwise the row's
   * first card is a shape donor only, and anything of its content the config
   * did not replace is dropped rather than shown under another product.
   */
  function buildReel(ctx, item, index) {
    var payload = shopPayloadFor(item);
    var firstHandle = payload && payload.p.length ? payload.p[0].h : '';
    var donor = firstHandle ? ctx.byHandle['#' + firstHandle] : null;
    var matched = !!donor;

    var card = (donor || ctx.proto).cloneNode(true);
    stripEditorAttrs(card);
    card.classList.remove('is-playing');
    card.removeAttribute('data-reel-index');
    card.style.setProperty('--soho-reel-i', String(index));
    card.style.setProperty('--soho-reel-pos', CROP[item.crop] || DEFAULT_CROP);

    var posterUrl = mediaSrc(item.posterUrl);
    var videoUrl = mediaSrc(item.videoUrl);

    /* -- poster ---------------------------------------------------------- */
    var posterBox = card.querySelector('.soho-reel__poster');
    if (posterBox) {
      var img = posterBox.querySelector('img');
      var placeholder = posterBox.querySelector('.svg-placeholder');
      if (posterUrl) {
        if (!img) {
          img = document.createElement('img');
          (posterBox.querySelector('figure') || posterBox).appendChild(img);
        }
        drop(placeholder);
        /* srcset/sizes describe the ORIGINAL photo's rungs. Left in place the
           browser would happily keep painting the old file. */
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.removeAttribute('width');
        img.removeAttribute('height');
        img.setAttribute('src', posterUrl);
        img.setAttribute('alt', item.caption || '');
        img.setAttribute('loading', 'lazy');
        img.setAttribute('decoding', 'async');
      } else if (!matched) {
        /* No cover and not this product's own card: show the section's media
           background rather than another product's photo. */
        drop(img);
        drop(placeholder);
      }
    }

    /* -- video ----------------------------------------------------------- */
    var media = card.querySelector('[data-soho-reel-media]') || card.querySelector('.soho-reel__media');
    var player = card.querySelector('[data-soho-reel-player]');
    var toggle = card.querySelector('[data-soho-reel-toggle]');

    if (videoUrl && media) {
      if (!player) {
        player = document.createElement('div');
        player.className = 'soho-reel__player';
        player.setAttribute('data-soho-reel-player', '');
        media.appendChild(player);
      }
      var tpl = player.querySelector('[data-soho-reel-template]');
      if (!tpl) {
        tpl = document.createElement('template');
        tpl.setAttribute('data-soho-reel-template', '');
      }

      /* Mute and Loop stay THEME settings: read them off the card we cloned so
         the dashboard cannot silently unmute a row (an unmuted video is refused
         autoplay by every browser, and the section warns about that in the
         editor — one owner for that rule, not two). */
      var old = tpl.content ? tpl.content.querySelector('video') : null;
      var wantMuted = old ? old.hasAttribute('muted') : true;
      var wantLoop = old ? old.hasAttribute('loop') : true;

      var video = document.createElement('video');
      video.setAttribute('playsinline', '');
      video.setAttribute('preload', 'none');
      video.setAttribute('tabindex', '-1');
      video.setAttribute('aria-hidden', 'true');
      if (wantMuted) video.setAttribute('muted', '');
      if (wantLoop) video.setAttribute('loop', '');
      if (posterUrl) video.setAttribute('poster', posterUrl);
      var source = document.createElement('source');
      source.setAttribute('src', videoUrl);
      video.appendChild(source);

      if (tpl.content) {
        while (tpl.content.firstChild) tpl.content.removeChild(tpl.content.firstChild);
        tpl.content.appendChild(video);
      } else {
        /* <template> without .content is a browser too old for any of this. */
        return null;
      }
      /* The player is emptied because the theme's own script may already have
         mounted the cloned card's <video> into it. */
      player.textContent = '';
      player.appendChild(tpl);

      if (!toggle) {
        toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'soho-reel__toggle';
        toggle.setAttribute('data-soho-reel-toggle', '');
        var play = document.createElement('span');
        play.className = 'soho-reel__play';
        play.setAttribute('aria-hidden', 'true');
        toggle.appendChild(play);
        media.appendChild(toggle);
      }
      toggle.setAttribute('aria-pressed', 'false');
      toggle.setAttribute('aria-label', (item.caption || 'Reel') + ' — play video');
    } else {
      drop(player);
      drop(toggle);
    }

    /* -- the bag, i.e. the way into the modal ----------------------------- */
    /* `soho-video-reels.js` delegates its click on [data-soho-reel-bag]: no
       bag, no modal, however good the payload is. So when the donor had none —
       a theme whose blocks have no product, which is precisely the store this
       feature exists for — one is built. The href stays a real PDP link, and
       stays the whole feature when the modal asset never loads (contract §1
       rule 4 and the reel player's own bail-out path both land there). */
    var href = primaryHref(payload) || safeHref(item.productUrl);
    var count = shopCount(payload);
    var bag = card.querySelector('[data-soho-reel-bag]') || card.querySelector('.soho-reel__bag');

    if (href) {
      if (!bag) {
        bag = buildBag(ctx.bagProto);
        (media || card).appendChild(bag);
      }
      bag.setAttribute('href', href);
      bag.setAttribute('aria-label', bagLabel(payload, item));
      /* §2: N is the number of shoppable destinations, and its presence is what
         says "this bag has a payload behind it". */
      if (count) bag.setAttribute('data-soho-shop-count', String(count));
      else bag.removeAttribute('data-soho-shop-count');
    } else {
      drop(bag);
    }

    /* -- caption + price -------------------------------------------------- */
    /* The rule that used to be wrong here, and what it costs: a field the
       dashboard did not send must never delete what Liquid rendered. But a
       clone carries the DONOR's caption and the DONOR's price, so "what Liquid
       rendered" is only this card's truth when the donor was matched by handle
       above. Matched: keep the theme's live money — the price snippet knows
       about compare-at, price_varies and sold out, and no config field does.
       Not matched: an unsent field leaves the card blank there, because another
       product's price under this video is worse than no price at all.
       Cents beat the legacy formatted string whenever both arrive: §1 rule 1
       exists because a pre-formatted string is what erased live Shopify money. */
    var captionText = str(item.caption, 160);
    var priceText = money(firstCents(payload)) || str(item.price, 40);
    var foot = card.querySelector('.soho-reel__foot');

    if ((captionText || priceText) && !foot) {
      foot = document.createElement('div');
      foot.className = 'soho-reel__foot';
      card.appendChild(foot);
    }

    if (foot) {
      var oldCaption = foot.querySelector('.soho-reel__caption');
      if (captionText) {
        /* Rebuilt rather than edited: with a destination it must be a link, and
           without one it must not be. */
        var caption = document.createElement(href ? 'a' : 'span');
        caption.className = 'soho-reel__caption';
        if (href) caption.setAttribute('href', href);
        caption.textContent = captionText;
        if (oldCaption) foot.replaceChild(caption, oldCaption);
        else foot.insertBefore(caption, foot.firstChild);
      } else if (!matched) {
        drop(oldCaption);
      } else if (oldCaption && href && oldCaption.tagName === 'A') {
        /* Same product, possibly a new destination (a collection card can have
           been added since the theme rendered this one). */
        oldCaption.setAttribute('href', href);
      }

      var price = foot.querySelector('.soho-reel__price');
      if (priceText) {
        if (!price) {
          price = document.createElement('span');
          price.className = 'soho-reel__price';
          foot.appendChild(price);
        }
        /* The theme's snippet renders live Shopify money as markup; a
           dashboard price is text and replaces the whole snippet. */
        price.textContent = priceText;
      } else if (!matched) {
        drop(price);
      }

      /* An overlay foot is absolutely positioned: an empty one still paints a
         scrim across the bottom of the photo. */
      if (!foot.querySelector('.soho-reel__caption') && !foot.querySelector('.soho-reel__price')) {
        drop(foot);
      }
    }

    /* Last, and never conditionally: a card with nothing to sell must LOSE the
       payload it inherited from the donor. */
    ctx.written += writeShopPayload(card, payload);

    return card;
  }

  function applyReels(root, cfg) {
    if (!cfg) return;

    if (cfg.enabled === false) {
      root.setAttribute('hidden', '');
      root.style.display = 'none';
      log('reels hidden by config', root.id);
      return;
    }

    setText(root.querySelector('.soho-reels__title'), cfg.heading);
    /* The editorial header puts the subheading in the eyebrow, the classic one
       in the sub. Only one of the two is ever in the DOM. */
    setText(root.querySelector('.soho-reels__eyebrow'), cfg.subheading);
    setText(root.querySelector('.soho-reels__sub'), cfg.subheading);

    var items = cfg.items || [];
    if (!items.length) {
      log('reels: no items in config, theme blocks kept', root.id);
      return;
    }

    var viewport = root.querySelector('soho-reels');
    var track = root.querySelector('[data-soho-reel-track]');
    var protos = track ? els(track, '[data-soho-reel]') : [];
    if (!viewport || !track || !protos.length) {
      log('reels: nothing to clone, left alone', root.id);
      return;
    }

    /* Which theme card is already this product's card. Built once per row: the
       lookup is what lets a rebuilt card keep the theme's live price instead of
       inheriting card 1's. */
    var byHandle = {};
    for (var p = 0; p < protos.length; p++) {
      var handle = cardHandle(protos[p]);
      if (handle && !byHandle['#' + handle]) byHandle['#' + handle] = protos[p];
    }

    var ctx = {
      proto: protos[0],
      /* Any card's bag will do as the glyph donor — it carries the theme's own
         icon and the storefront's own classes. */
      bagProto: root.querySelector('[data-soho-reel-bag]') || root.querySelector('.soho-reel__bag'),
      byHandle: byHandle,
      written: 0
    };

    var snapshot = track.innerHTML;
    try {
      var cards = [];
      for (var i = 0; i < items.length; i++) {
        var card = buildReel(ctx, items[i], i);
        if (card) cards.push(card);
      }
      if (!cards.length) throw new Error('no cards built');

      track.textContent = '';
      for (var c = 0; c < cards.length; c++) track.appendChild(cards[c]);

      /* Re-run the section's own player over the new cards. If the theme never
         loaded it (a stills-only row that has now been given videos), loading
         it is enough: customElements.define() upgrades the element in place. */
      if (window.customElements && customElements.get('soho-reels')) reattach(viewport);
      else loadOnce(REELS_JS);

      SHOP_WRITTEN += ctx.written;
      log('reels rebuilt', root.id, cards.length + ' cards', ctx.written + ' shoppable targets');
    } catch (err) {
      track.innerHTML = snapshot;
      if (window.customElements && customElements.get('soho-reels')) reattach(viewport);
      log('reels rebuild failed, theme restored', err);
    }
  }

  /* ------------------------------------------------------------- the dock -- */

  /**
   * One story bubble. Same clone-the-theme rule as the row, and the same
   * payload rule: the shop bar is NOT cloned any more. The dock's player reads
   * `<script data-soho-shop>` off this <li> and asks
   * `window.SohoShopVideo.mountTray()` to draw the strip inside its own buy
   * bar, which is the section's permanent `[data-soho-dock-buy]` row — so a
   * dashboard story sells the whole outfit even in a store where not one theme
   * block ever had a product on it.
   */
  function buildDockItem(proto, item, index) {
    var li = proto.cloneNode(true);
    stripEditorAttrs(li);
    li.classList.remove('is-seen');
    /* The Device select is a THEME rule about theme blocks; a dashboard story
       is simply on. */
    li.classList.remove('soho-dock__item--mobile');
    li.classList.remove('soho-dock__item--desktop');

    var label = item.label || item.productTitle || 'Watch';
    var posterUrl = mediaSrc(item.posterUrl);
    var videoUrl = mediaSrc(item.videoUrl);
    /* A bubble IS a poster and a slide IS one of the two. With neither there is
       nothing to tap and nothing to play. */
    if (!posterUrl && !videoUrl) return null;

    var payload = shopPayloadFor(item);
    var href = primaryHref(payload) || safeHref(item.productUrl);

    var button = li.querySelector('[data-soho-dock-open]');
    if (!button) return null;
    button.setAttribute('data-soho-dock-open', String(index));
    button.setAttribute('aria-label', 'Play story: ' + label);
    button.setAttribute('title', label);

    var bubble = li.querySelector('.soho-dock__poster');
    var mediaBox = li.querySelector('.soho-dock__media');
    if (posterUrl) {
      if (!bubble && mediaBox) {
        bubble = document.createElement('img');
        bubble.className = 'soho-dock__poster';
        mediaBox.appendChild(bubble);
      }
      if (bubble) {
        bubble.removeAttribute('srcset');
        bubble.removeAttribute('sizes');
        bubble.setAttribute('src', posterUrl);
        bubble.setAttribute('alt', '');
        bubble.setAttribute('loading', 'lazy');
        bubble.setAttribute('decoding', 'async');
      }
    } else {
      /* A video story with no cover: the disc keeps the section's own empty
         surface instead of the previous bubble's face. */
      drop(bubble);
    }

    setText(li.querySelector('.soho-dock__caption'), label);

    /* The bag glyph stays a plain link — it is a sibling <a> outside the
       bubble's <button>, it is what a shopper who never opens the story taps,
       and the strip lives inside the player where there is room for it. */
    var glyph = li.querySelector('.soho-dock__glyph');
    if (glyph) {
      if (href) {
        glyph.setAttribute('href', href);
        glyph.setAttribute('aria-label', bagLabel(payload, item));
      } else {
        drop(glyph);
      }
    }

    /* -- the slide -------------------------------------------------------- */
    var tpl = li.querySelector('template[data-soho-dock-slide]');
    if (!tpl || !tpl.content) return null;
    tpl.setAttribute('data-soho-dock-slide', String(index));
    tpl.setAttribute('data-device', 'all');
    tpl.setAttribute('data-title', label);
    while (tpl.content.firstChild) tpl.content.removeChild(tpl.content.firstChild);

    var slide = document.createElement('div');
    slide.className = 'soho-dock__slide';
    if (videoUrl) {
      var video = document.createElement('video');
      video.className = 'soho-dock__video';
      video.setAttribute('preload', 'none');
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('disableremoteplayback', '');
      if (posterUrl) video.setAttribute('poster', posterUrl);
      var source = document.createElement('source');
      source.setAttribute('src', videoUrl);
      video.appendChild(source);
      slide.appendChild(video);
    } else {
      var still = document.createElement('img');
      still.className = 'soho-dock__still';
      still.setAttribute('src', posterUrl);
      still.setAttribute('alt', label);
      still.setAttribute('loading', 'lazy');
      still.setAttribute('decoding', 'async');
      slide.appendChild(still);
    }
    tpl.content.appendChild(slide);

    /* §1: one payload per bubble, a direct child of the card. Written last and
       unconditionally, so a story with nothing to sell also loses the payload
       it inherited from the donor bubble. */
    return { li: li, written: writeShopPayload(li, payload) };
  }

  function applyDock(el, cfg) {
    if (!cfg) return;

    if (cfg.enabled === false) {
      try {
        if (el.open && typeof el.close === 'function') el.close(true);
      } catch (e) {
        /* closing is a courtesy; hiding is the job */
      }
      el.setAttribute('hidden', '');
      el.style.display = 'none';
      log('dock hidden by config');
      return;
    }

    var items = cfg.items || [];
    if (!items.length) {
      log('dock: no items in config, theme blocks kept');
      return;
    }

    var list = el.querySelector('.soho-dock__list');
    var proto = list && list.querySelector('.soho-dock__item');
    if (!list || !proto || !proto.querySelector('[data-soho-dock-open]')) {
      log('dock: nothing to clone, left alone');
      return;
    }

    var snapshot = list.innerHTML;
    try {
      if (el.open && typeof el.close === 'function') el.close(true);

      var built = [];
      var written = 0;
      for (var i = 0; i < items.length; i++) {
        var made = buildDockItem(proto, items[i], built.length);
        if (!made) continue;
        written += made.written;
        built.push(made.li);
      }
      if (!built.length) throw new Error('no bubbles built');

      list.textContent = '';
      for (var b = 0; b < built.length; b++) list.appendChild(built[b]);

      /* The dock boots once and guards itself against a second boot, so it
         cannot simply be re-attached like the reel row. Its two published
         re-read points are enough: the slide deck and the progress bars. */
      if (typeof el.refreshSlides === 'function' && typeof el.buildBars === 'function') {
        el.allSlides = els(el, 'template[data-soho-dock-slide]');
        el.refreshSlides();
        el.buildBars();
      }
      SHOP_WRITTEN += written;
      log('dock rebuilt', built.length + ' stories', written + ' shoppable targets');
    } catch (err) {
      list.innerHTML = snapshot;
      try {
        if (typeof el.refreshSlides === 'function' && typeof el.buildBars === 'function') {
          el.allSlides = els(el, 'template[data-soho-dock-slide]');
          el.refreshSlides();
          el.buildBars();
        }
      } catch (e) {
        /* the markup is back either way */
      }
      log('dock rebuild failed, theme restored', err);
    }
  }

  /* --------------------------------------------------------------- fetch -- */

  /**
   * Which page this is, in the vocabulary the route filters `show_on` with.
   * Read from the body class the theme already sets; if we cannot tell we send
   * nothing and the server does not filter.
   */
  function pageName() {
    try {
      var b = document.body.className || '';
      if (/(^|\s)template-index(\s|$)/.test(b)) return 'home';
      if (/(^|\s)template-product(\s|$)/.test(b)) return 'product';
      if (/(^|\s)template-collection(\s|$)/.test(b)) return 'collection';
    } catch (e) {
      /* no page hint; the server returns everything */
    }
    return '';
  }

  var PAGE = pageName();

  /* The page is IN THE KEY, and that is a correctness rule rather than tidiness:
     the answer is filtered by `show_on` server-side, so one cache entry shared
     by every page served the home page's videos on a product page for the next
     minute — `show_on` was simply not enforced for 60 seconds at a time. `v2`
     is the payload generation: an entry cached before the products[] round has
     no shoppable data in it and must not be read as though it had. */
  var CACHE_KEY = 'soho:video-config:v2:' + (PAGE || 'any');

  /** How many shoppable targets this pass wrote onto the page. */
  var SHOP_WRITTEN = 0;

  function readCache() {
    try {
      var raw = window.sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.at !== 'number') return null;
      if (Date.now() - parsed.at > CACHE_MS) return null;
      return parsed.body;
    } catch (e) {
      return null;
    }
  }

  function writeCache(body) {
    try {
      window.sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), body: body }));
    } catch (e) {
      /* private mode, quota, whatever: the cache is an optimisation */
    }
  }

  function load() {
    var cached = readCache();
    if (cached) {
      log('config from sessionStorage', CACHE_KEY);
      return Promise.resolve(cached);
    }

    var url =
      ENDPOINT +
      (ENDPOINT.indexOf('?') > -1 ? '&' : '?') +
      'shop=' +
      encodeURIComponent(window.location.hostname) +
      (PAGE ? '&page=' + PAGE : '');

    var controller = null;
    var timer = null;
    var options = { method: 'GET', credentials: 'omit', mode: 'cors' };
    if (window.AbortController) {
      controller = new AbortController();
      options.signal = controller.signal;
      /* A dashboard that is slow, asleep or gone must cost the storefront a
         couple of seconds of a background request and nothing else. */
      timer = window.setTimeout(function () {
        controller.abort();
      }, TIMEOUT_MS);
    }

    return fetch(url, options)
      .then(function (res) {
        if (timer) window.clearTimeout(timer);
        if (!res.ok) throw new Error('http ' + res.status);
        return res.json();
      })
      .then(function (body) {
        if (!body || body.ok !== true) throw new Error('bad body');
        writeCache(body);
        return body;
      });
  }

  /* ---------------------------------------------------------------- boot -- */

  var reelRoots = els(document, '[id^="SohoReels--"]');
  var dockRoots = els(document, 'soho-story-dock');
  /* No surface on this page: no request. Most pages are in this branch. */
  if (!reelRoots.length && !dockRoots.length) return;

  load()
    .then(function (body) {
      if (body.configured !== true || !body.config) {
        log('nothing configured', body.reason || '');
        return;
      }
      var config = body.config;
      log('config', config);

      for (var i = 0; i < reelRoots.length; i++) {
        try {
          applyReels(reelRoots[i], config.reels);
        } catch (err) {
          log('reels apply threw', err);
        }
      }
      for (var d = 0; d < dockRoots.length; d++) {
        try {
          applyDock(dockRoots[d], config.dock);
        } catch (err) {
          log('dock apply threw', err);
        }
      }

      /* The dashboard put something on sale that the theme never rendered, and
         the section may not have loaded the modal because none of ITS blocks
         had a product. The player is looked up at click time by both surfaces,
         so loading it late is enough — and with no `data-shop-js` on the tag
         this is a no-op, never a guessed asset URL. */
      if (SHOP_WRITTEN && !window.SohoShopVideo) loadOnce(SHOP_JS);
    })
    .catch(function (err) {
      /* The one and only outcome of every failure: the theme keeps the page. */
      log('config unavailable, theme kept', err && err.message);
    });
})();
